import { FILTER_VEHICLE_TYPES_VANS } from 'lib/constants'
import React from 'react'
import { useMediaQuery } from 'react-responsive'

import {
  fastestChargeTimeForElectricVehicle,
  fuelConsumptionValue,
  rangeForElectricVehicle,
} from '../../../../lib/utilities/vehicle'
import { VehicleDataSpotlight, VehicleDataSpotlightProps } from './VehicleDataSpotlight'
import styles from './vehicle-data-spotlight.module.scss'
import { TechnicalData } from '@/types/vehicle'

export type VehicleDataSpotlightGroupProps = {
  technicalData: TechnicalData
  vehicleFuelType: number
  vehicleType: number
  isAdPage?: boolean
}

export const VehicleDataSpotlightGroup = ({
  technicalData,
  vehicleFuelType,
  vehicleType,
  isAdPage = false,
}: VehicleDataSpotlightGroupProps) => {
  const isLargeTablet = useMediaQuery({
    maxWidth: vehicleType === FILTER_VEHICLE_TYPES_VANS ? 1680 : 1600,
  })
  const isSmallTablet = useMediaQuery({
    maxWidth: vehicleType === FILTER_VEHICLE_TYPES_VANS ? 1540 : 1490,
  })
  const isLargeMobile = useMediaQuery({
    maxWidth: vehicleType === FILTER_VEHICLE_TYPES_VANS ? 1400 : 1350,
  })
  const isMediumMobile = useMediaQuery({ maxWidth: 1260 })
  const isMobile = useMediaQuery({ maxWidth: 991 })

  const electricDataPoints = [
    {
      metric: 'miles',
      value: rangeForElectricVehicle(technicalData).displayValue
        ? rangeForElectricVehicle(technicalData).displayValue
        : '---',
      label: 'Range',
    },
    {
      metric: 'mins',
      value: fastestChargeTimeForElectricVehicle(technicalData).displayValue
        ? fastestChargeTimeForElectricVehicle(technicalData).displayValue
        : '---',
      label: 'Charging time',
    },
  ]

  const iceDataPoints = [
    {
      metric: 'secs',
      value: technicalData[12]?.displayValue ? technicalData[12].displayValue : '---',
      label: '0-62',
    },
    {
      metric: 'mpg',
      value: fuelConsumptionValue(technicalData).displayValue
        ? fuelConsumptionValue(technicalData).displayValue
        : '---',
      label: 'Fuel consumption',
    },
  ]

  const commonCarDataPoints = [
    ...(vehicleFuelType === 5 ? electricDataPoints : iceDataPoints),
    {
      metric: 'mph',
      value: technicalData[13]?.displayValue ? technicalData[13].displayValue : '---',
      label: 'Top speed',
    },
    {
      metric: 'bhp',
      value: technicalData[21]?.displayValue ? technicalData[21].displayValue : '---',
      label: 'Power',
    },
    {
      metric: '',
      value: technicalData[133]?.displayValue ? technicalData[133].displayValue : '---',
      label: 'Insurance group',
    },
    {
      metric: 'litres',
      value: technicalData[40]?.displayValue ? technicalData[40].displayValue : '---',
      label: 'Boot space',
    },
  ]

  const commonVanDataPoints = [
    vehicleFuelType === 5
      ? {
        metric: 'miles',
        value: rangeForElectricVehicle(technicalData).displayValue
          ? rangeForElectricVehicle(technicalData).displayValue
          : '---',
        label: 'Range',
      }
      : {
        metric: 'mpg',
        value: fuelConsumptionValue(technicalData).displayValue
          ? fuelConsumptionValue(technicalData).displayValue
          : '---',
        label: 'Fuel consumption',
      },
    {
      metric: 'tonnes',
      value: technicalData[14]?.displayValue
        ? (Number(technicalData[14].displayValue) / 1000).toFixed(2)
        : '---',
      label: 'Weight',
    },
    {
      metric: 'tonnes',
      value: technicalData[32]?.displayValue
        ? (Number(technicalData[32].displayValue) / 1000).toFixed(2)
        : '---',
      label: 'Payload',
    },
    {
      metric: 'metres',
      value: technicalData[36]?.displayValue
        ? (Number(technicalData[36].displayValue) / 1000).toFixed(2)
        : '---',
      label: 'Load length',
    },
    {
      metric: 'metres',
      value: technicalData[38]?.displayValue
        ? (Number(technicalData[38].displayValue) / 1000).toFixed(2)
        : '---',
      label: 'Load height',
    },
    {
      metric: 'metres',
      value: technicalData[37]?.displayValue
        ? (Number(technicalData[37].displayValue) / 1000).toFixed(2)
        : '---',
      label: 'Load width',
    },
  ]

  const dataPoints =
    vehicleType === FILTER_VEHICLE_TYPES_VANS ? commonVanDataPoints : commonCarDataPoints

  const responsiveDataPoints = () => {
    let numberOfVisiblePoints = dataPoints.length

    if (isMobile) {
      return dataPoints
    }

    if (isLargeTablet) {
      numberOfVisiblePoints -= 1
    }

    if (isSmallTablet) {
      numberOfVisiblePoints -= 1
    }

    if (isLargeMobile) {
      numberOfVisiblePoints -= 1
    }

    if (isMediumMobile) {
      numberOfVisiblePoints -= 1
    }

    return dataPoints.slice(0, numberOfVisiblePoints)
  }

  return (
    <div className={isAdPage ? styles.list : styles.container}>
      {responsiveDataPoints().map((point: VehicleDataSpotlightProps, index: number) => {
        return (
          <React.Fragment key={point.label}>
            {index !== 0 && <div className={styles.divider}></div>}
            <VehicleDataSpotlight
              metric={point.metric}
              value={point.value}
              label={point.label}
              isAdPage={isAdPage}
            />
          </React.Fragment>
        )
      })}
    </div>
  )
}
